<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import { useKeyboardStore } from '@/stores/keyboardStore';
import { useToast } from 'vue-toastification';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import Select from 'primevue/select';
import { useQzStore } from '@/stores/qzStore';
import qz from 'qz-tray';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import ToggleButton from 'primevue/togglebutton';
import Checkbox from 'primevue/checkbox';

const checked = ref(true);
const showPrinterConfig = ref(false);

const authStore = useAuthStore();
const orderStore = useOrderStore();
const productStore = useProductStore();
const keyboardStore = useKeyboardStore();
const showBranchInfo = ref(false);
const branchName = ref('');
const isLoadingBranchName = ref(false);
const downloadedFileNames = ref([]);
const toast = useToast();
const qzStore = useQzStore();

const availableSizes = ref([]);
const availableTrays = ref([]);
const selectedSize = ref(null);
const selectedTray = ref(null);
const isLoadingPrinterSizes = ref(false);

// Barcode print 

const availableBarcodeSizes = ref([]);
const availableBarcodeTrays = ref([]);
const selectedBarcodeSize = ref(null);
const selectedBarcodeTray = ref(null);
const isLoadingBarcodePrinterSizes = ref(false);
const isPrintWithoutPriceChecked = ref(false);

const togglePrinterMode = async () => {
  // Prevent action if connection is already in progress
  if (qzStore.isConnecting) {
    console.log("QZ connection in progress, please wait...");
    return;
  }

  if (checked.value) {
    // Checkbox is checked - establish QZ connection
    if (!qzStore.isQzConnected) {
      try {
        await qzStore.connectQZ(true);
        if (qzStore.isQzConnected) {
          qzStore.useQzTrayPrint = true;
          toast.success("QZ Tray connected and enabled for printing");
        }
      } catch (error) {
        console.error("Failed to connect QZ Tray:", error);
        toast.error("Failed to connect QZ Tray. Please ensure QZ Tray is running.");
        checked.value = false;
      }
    } else {
      // QZ is already connected, just enable QZ printing
      qzStore.useQzTrayPrint = true;
    }
    
    // Store the user's preference to enable QZ printing
    localStorage.setItem("useQzTrayPrint", "true");
  } else {
    // Checkbox is unchecked - stop QZ connection and use Windows print only
    if (qzStore.isQzConnected) {
      try {
        await qzStore.disconnectQZ();
        toast.info("QZ Tray disconnected. Using Windows print only.");
      } catch (error) {
        console.error("Failed to disconnect QZ Tray:", error);
        toast.error("Failed to disconnect QZ Tray.");
      }
    }
    qzStore.useQzTrayPrint = false;
    
    // Store the user's preference to disable QZ printing
    localStorage.setItem("useQzTrayPrint", "false");
  }
};

// Format printers into { label, value }
const printerOptions = computed(() =>
  qzStore.allPrinters.map(p => ({ label: p, value: p }))
);

const toggleBranchInfo = async () => {
  if (!(authStore.isUserLoggedIn || authStore.isManagerLoggedIn)) return;
  
  showBranchInfo.value = !showBranchInfo.value;
  
  if (showBranchInfo.value && productStore.branchId) {
    if (!branchName.value) {
      extractBranchName();
    }
    await fetchDownloadedFiles();
  }
};

const extractBranchName = () => {
  try {
    isLoadingBranchName.value = true;
    
    // Check if currentBranch is available in the global scope
    if (window.currentBranch && window.currentBranch.Name) {
      branchName.value = window.currentBranch.Name;
    } else {
      branchName.value = 'Unknown Branch';
    }
  } catch (error) {
    console.error('Error extracting branch name:', error);
    branchName.value = 'Error loading name';
  } finally {
    isLoadingBranchName.value = false;
  }
};

const fetchDownloadedFiles = async () => {
  try {
    if (!productStore.branchId || !productStore.userId) return;
    
    const response = await fetch(`/api/File/GetFiles?branchId=${productStore.branchId}&userId=${productStore.userId}`, {
      headers: {
        'store-id': productStore.branchId,
      },
    });
    
    if (!response.ok) throw new Error('Failed to fetch files');
    
    const files = await response.json();
    // Filter for .js files and extract just the filenames
    downloadedFileNames.value = files
      .filter(file => file.fileUrl.endsWith('.js'))
      .map(file => file.fileUrl.split('/').pop());
    
    // Log the first file name if available
    if (downloadedFileNames.value.length > 0) {
      // console.log('First file name:', downloadedFileNames.value[0]);
      
      const itemListFile = downloadedFileNames.value.find(name => name.includes('itemlist'));
      if (itemListFile) {
        orderStore.resetInventoryState(itemListFile);
      }
    }
  } catch (error) {
    console.error('Error fetching downloaded files:', error);
    downloadedFileNames.value = ['Error loading file list'];
  }
};

const downloadedFilesSummary = computed(() => {
  return {
    productFiles: productStore.products.length,
    taxFiles: productStore.taxes.length,
    loadingPercentage: productStore.loadingPercentage,
    isLoading: productStore.isLoading
  };
});

// Watch for changes in store's useQzTrayPrint to keep toggle in sync
watch(() => qzStore.useQzTrayPrint, (newValue) => {
  checked.value = newValue;
});

// Watch for QZ connection status changes
watch(() => qzStore.isQzConnected, (isConnected) => {
  if (!isConnected) {
    // When QZ disconnects, reset printer mode to false
    qzStore.useQzTrayPrint = false;
    checked.value = false;
  } else {
    // When QZ connects, only enable QZ printing if user hasn't explicitly disabled it
    const savedPreference = localStorage.getItem("useQzTrayPrint");
    const userWantsQzPrint = savedPreference === "true";
    
    if (userWantsQzPrint) {
      qzStore.useQzTrayPrint = true;
      checked.value = true;
    }
  }
});

watch(  
  () => qzStore.selectedPrinter,
  async (newPrinter) => {
    if (!newPrinter || !newPrinter.value) {
      availableSizes.value = [];
      availableTrays.value = [];
      return;
    }

    if (!qzStore.isQzConnected) {
      availableSizes.value = [];
      availableTrays.value = [];
      return;
    }

    try {
      isLoadingPrinterSizes.value = true;
      await qzStore.getPrinterDetails();
      const details = qzStore.printerDetails;

      // Handle sizes
      availableSizes.value = details?.sizes?.map((s) => ({
        label: s.name,
        value: s.name,
      })) || [];

      // Handle trays
      const rawTrays = details?.trays || [];
      availableTrays.value = rawTrays.map((tray) => {
        if (typeof tray === 'string') {
          return { label: tray, value: tray };
        } else if (tray && typeof tray === 'object' && tray.name) {
          return { label: tray.name, value: tray.name };
        } else {
          return { label: String(tray), value: String(tray) };
        }
      });

      // FIX: Use the store's selectedSizeConfig and selectedTrayConfig which are already persisted
      if (qzStore.selectedSizeConfig && availableSizes.value.some(s => s.value === qzStore.selectedSizeConfig.value)) {
        selectedSize.value = qzStore.selectedSizeConfig;
      } else if (availableSizes.value.length > 0) {
        selectedSize.value = availableSizes.value[0];
      }

      if (qzStore.selectedTrayConfig && availableTrays.value.some(t => t.value === qzStore.selectedTrayConfig.value)) {
        selectedTray.value = qzStore.selectedTrayConfig;
      } else if (availableTrays.value.length > 0) {
        selectedTray.value = availableTrays.value[0];
      }

    } catch (error) {
      console.error("Error loading printer sizes:", error);
      availableSizes.value = [];
      selectedSize.value = null;
      availableTrays.value = [];
      selectedTray.value = null;
    } finally {
      isLoadingPrinterSizes.value = false;
    }
  },
  { immediate: true }
);

watch([selectedSize, selectedTray], ([newSize, newTray]) => {
  qzStore.setSelectedPrinterSizeWidthHeightandTray(newSize, newTray);
});

// Barcode printer watcher

watch(
  () => qzStore.selectedBarcodePrinter,
  async (newPrinter) => {
    if (!newPrinter || !newPrinter.value) {
      availableBarcodeSizes.value = [];
      availableBarcodeTrays.value = [];
      return;
    }

    if (!qzStore.isQzConnected) {
      availableBarcodeSizes.value = [];
      availableBarcodeTrays.value = [];
      return;
    }

    try {
      isLoadingBarcodePrinterSizes.value = true;
      await qzStore.getBarcodePrinterDetails();
      const details = qzStore.barcodePrinterDetails;

      // Handle sizes
      availableBarcodeSizes.value = details?.sizes?.map((s) => ({
        label: s.name,
        value: s.name,
      })) || [];

      // Handle trays
      const rawTrays = details?.trays || [];
      availableBarcodeTrays.value = rawTrays.map((tray) => {
        if (typeof tray === 'string') {
          return { label: tray, value: tray };
        } else if (tray && typeof tray === 'object' && tray.name) {
          return { label: tray.name, value: tray.name };
        } else {
          return { label: String(tray), value: String(tray) };
        }
      });

      // FIX: Use the store's selectedBarcodeSizeConfig and selectedBarcodeTrayConfig which are already persisted
      if (qzStore.selectedBarcodeSizeConfig && availableBarcodeSizes.value.some(s => s.value === qzStore.selectedBarcodeSizeConfig.value)) {
        selectedBarcodeSize.value = qzStore.selectedBarcodeSizeConfig;
      } else if (availableBarcodeSizes.value.length > 0) {
        selectedBarcodeSize.value = availableBarcodeSizes.value[0];
      }

      if (qzStore.selectedBarcodeTrayConfig && availableBarcodeTrays.value.some(t => t.value === qzStore.selectedBarcodeTrayConfig.value)) {
        selectedBarcodeTray.value = qzStore.selectedBarcodeTrayConfig;
      } else if (availableBarcodeTrays.value.length > 0) {
        selectedBarcodeTray.value = availableBarcodeTrays.value[0];
      }

    } catch (error) {
      console.error("Error loading barcode printer sizes:", error);
      availableBarcodeSizes.value = [];
      selectedBarcodeSize.value = null;
      availableBarcodeTrays.value = [];
      selectedBarcodeTray.value = null;
    } finally {
      isLoadingBarcodePrinterSizes.value = false;
    }
  },
  { immediate: true }
);

watch([selectedBarcodeSize, selectedBarcodeTray], ([newSize, newTray]) => {
  qzStore.setSelectedBarcodePrinterSizeWidthHeightandTray(newSize, newTray);
});

// Function to check QZ connection status
const checkQzConnection = () => {
  if (typeof qz !== 'undefined') {
    const wasConnected = qzStore.isQzConnected;
    const isNowConnected = qz.websocket.isActive();
    
    // Only update if the connection status actually changed
    if (wasConnected !== isNowConnected) {
      qzStore.isQzConnected = isNowConnected;
    }
  }
};

onMounted(() => {
  tippy('#SettingTooltip', {
    content: 'Settings',
    animation: 'scale',
  });

  // Initialize printer mode and connection status from store
  qzStore.initPrinterMode();
  qzStore.initQzConnectionStatus();
  qzStore.initPrinterSettings();
  qzStore.initBarcodePrinterSettings();
  qzStore.initSelectedBarcodePrinter();

  if (qzStore.selectedPrinter && qzStore.isQzConnected) {
    qzStore.getPrinterDetails();
  }
  
  if (qzStore.selectedBarcodePrinter && qzStore.isQzConnected) {
    qzStore.getBarcodePrinterDetails();
  }
  // Set initial checkbox state based on user preference and QZ connection status
  // Use a small delay to ensure QZ is fully initialized
  setTimeout(() => {
    // Get the user's previous preference from localStorage
    const savedPreference = localStorage.getItem("useQzTrayPrint");
    const userWantsQzPrint = savedPreference === "true";
    
    if (qzStore.isQzConnected && userWantsQzPrint) {
      // QZ is connected and user wants QZ printing
      qzStore.useQzTrayPrint = true;
      checked.value = true;
    } else if (qzStore.isQzConnected && !userWantsQzPrint) {
      // QZ is connected but user previously disabled it - disconnect QZ
      qzStore.disconnectQZ();
      qzStore.useQzTrayPrint = false;
      checked.value = false;
    } else if (!qzStore.isQzConnected && userWantsQzPrint && !qzStore.isConnecting) {
      // QZ is not connected but user wants QZ printing - try to connect
      qzStore.connectQZ(true).then(() => {
        if (qzStore.isQzConnected) {
          qzStore.useQzTrayPrint = true;
          checked.value = true;
        }
      });
    } else {
      // QZ is not connected and user doesn't want QZ printing
      // Don't connect to QZ at all
      qzStore.useQzTrayPrint = false;
      checked.value = false;
    }
  }, 100);

  // Set up periodic connection check
  const connectionCheckInterval = setInterval(checkQzConnection, 5000);

  // Clean up interval on unmount
  onUnmounted(() => {
    clearInterval(connectionCheckInterval);
  });

  // keyboardStore.register('Alt+F5', () => {
  //   orderStore.clearCache();
  //   // toast.info('Show Aad Item popup');
  // });
});

function togglePrintWithoutPrice () {
  if (isPrintWithoutPriceChecked.value) {
    qzStore.printWithoutPrice = true;
    console.log(qzStore.printWithoutPrice)
  } else {
    qzStore.printWithoutPrice = false;
    console.log(qzStore.printWithoutPrice)
  }
}

</script>

<template>
  <div id="SettingTooltip">
    <i @click="toggleBranchInfo" :disabled="!(authStore.isUserLoggedIn || authStore.isManagerLoggedIn)"
      :class="{ 'opacity-50 cursor-not-allowed': !(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) }"
      class="pi pi-cog text-center text-purple-500 cursor-pointer" style="font-size: 1.875rem;"></i>

    <!-- Branch Info Popup -->
    <div v-if="showBranchInfo" class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full shadow-xl max-h-[80vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4 relative z-50">
          <h3 class="text-lg font-semibold">Branch Information</h3>
          <button @click="toggleBranchInfo" class="text-gray-500 hover:text-gray-700">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <!--Show available printers-->
        <div class="card flex flex-row justify-center items-center gap-4">
          <span>Use QZ Tray Print</span>
          <div class="flex flex-col items-center gap-2">
            <Checkbox @change="togglePrinterMode" v-model="checked" :invalid="!checked" :disabled="qzStore.isConnecting"
              :class="['w-full sm:w-40 custom-checkbox', qzStore.isQzConnected ? 'checkbox-connected' : 'checkbox-disconnected']"
              aria-label="Confirmation" binary />
            <div v-if="qzStore.isConnecting" class="text-xs text-yellow-500 text-center">
              <i class="pi pi-spinner pi-spin mr-1"></i>Connecting to QZ Tray...
            </div>
          </div>
        </div>
        <button @click="showPrinterConfig = true" :disabled="!qzStore.useQzTrayPrint"
          class="px-4 py-2 mt-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed">
          Printer Settings
        </button>

        <!-- Printer Config Popup -->
        <transition name="slide-right">
          <div v-if="showPrinterConfig && qzStore.useQzTrayPrint" class="fixed inset-0 flex justify-end bg-opacity-50">
            <div class="bg-white h-full w-1/4 px-6 py-4 shadow-2xl relative overflow-y-auto rounded-none">
              <button @click="showPrinterConfig = false"
                class="absolute top-2 right-2 text-gray-500 hover:text-gray-700">
                <i class="pi pi-times"></i>
              </button>

              <h3 class="text-lg font-semibold mb-4">Printer Configuration</h3>
              <div class="card">
                <Accordion :value="['0']" multiple>
                  <!-- RECEIPT PRINTER PANEL -->
                  <AccordionPanel value="1">
                    <AccordionHeader>Receipt Printer</AccordionHeader>
                    <AccordionContent>
                      <div class="bg-gray-100 rounded pb-3 px-3 receipt-printer-settings-div">
                        <!-- <label for="printer" class="text-sm text-gray-600 pb-2">Receipt Printer</label> -->
                        <div class="py-2 flex flex-col relative z-50">
                          <Select :disabled="!qzStore.useQzTrayPrint" v-model="qzStore.selectedPrinter"
                            :options="qzStore.allPrinters" optionLabel="label" placeholder="Select Printer"
                            class="font-medium" />
                        </div>

                        <div class="flex flex-col gap-3">
                          <!-- Paper Size -->
                          <div class="flex gap-2">
                            <label for="paperSize" class="w-28 text-left text-sm text-gray-600">Paper Size</label>
                            <Select v-model="selectedSize" :options="availableSizes" optionLabel="label"
                              placeholder="Select Paper Size" class="flex-1 font-medium"
                              :loading="isLoadingPrinterSizes" />
                          </div>

                          <!-- Tray -->
                          <div class="flex gap-2">
                            <label for="tray" class="w-28 text-left text-sm text-gray-600">Trays</label>
                            <Select v-model="selectedTray" :options="availableTrays" optionLabel="label"
                              placeholder="Select Tray" class="flex-1 font-medium" />
                          </div>
                        </div>

                        <div class="flex flex-col mt-4 gap-4">
                          <!-- Margins -->
                          <div class="flex items-start gap-2">
                            <label class="w-28 text-sm text-gray-600 text-left mt-1">Margins (in)</label>
                            <div class="flex flex-col gap-2 flex-1 max-w-[400px]">
                              <input v-model.number="qzStore.selectedMarginConfig.top" type="number" placeholder="Top"
                                class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedMarginConfig.right" type="number"
                                placeholder="Right" class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedMarginConfig.bottom" type="number"
                                placeholder="Bottom" class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedMarginConfig.left" type="number" placeholder="Left"
                                class="border px-2 py-2 w-full rounded" />
                            </div>
                          </div>

                          <!-- Scale -->
                          <div class="flex gap-2">
                            <label class="w-28 text-sm text-gray-600 text-left">Scale (%)</label>
                            <div class="flex-1 max-w-[400px]">
                              <input v-model.number="qzStore.selectedScaleConfig" type="number" min="10" max="200"
                                class="border px-2 py-2 rounded w-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionPanel>

                  <!-- BARCODE PRINTER PANEL -->
                  <AccordionPanel value="2">
                    <AccordionHeader>Barcode Printer</AccordionHeader>
                    <AccordionContent>
                      <div class="bg-gray-100 rounded pb-3 px-3 barcode-printer-settings-div">
                        <!-- <label for="printer" class="text-sm text-gray-600">Barcode Printer</label> -->
                        <div class="py-2 flex flex-col relative z-50">
                          <Select :disabled="!qzStore.useQzTrayPrint" v-model="qzStore.selectedBarcodePrinter"
                            :options="qzStore.allPrinters" optionLabel="label" placeholder="Select Printer"
                            class="font-medium" />
                        </div>

                        <div class="flex flex-col gap-3">
                          <!-- Paper Size -->
                          <div class="flex gap-2">
                            <label for="paperSize" class="w-28 text-left text-sm text-gray-600">Paper Size</label>
                            <Select v-model="selectedBarcodeSize" :options="availableBarcodeSizes" optionLabel="label"
                              placeholder="Select Paper Size" class="flex-1 font-medium"
                              :loading="isLoadingPrinterSizes" />
                          </div>

                          <!-- Tray -->
                          <div class="flex gap-2">
                            <label for="tray" class="w-28 text-left text-sm text-gray-600">Trays</label>
                            <Select v-model="selectedBarcodeTray" :options="availableBarcodeTrays" optionLabel="label"
                              placeholder="Select Tray" class="flex-1 font-medium" />
                          </div>
                        </div>

                        <div class="flex justify-between items-center pt-3">
                          <label class="text-sm text-gray-600" for="">Include Price</label>
                          <Checkbox @change="togglePrintWithoutPrice" v-model="isPrintWithoutPriceChecked" :invalid="!checked" :disabled="qzStore.isConnecting"
                          :class="['w-full sm:w-40 custom-checkbox', qzStore.printWithoutPrice ? 'checkbox-connected' : 'checkbox-disconnected']"
                          aria-label="Confirmation" binary />
                        </div>

                        <div class="flex flex-col mt-4 gap-4">
                          <!-- Margins -->
                          <div class="flex items-start gap-2">
                            <label class="w-28 text-sm text-gray-600 text-left mt-1">Margins (in)</label>
                            <div class="flex flex-col gap-2 flex-1 max-w-[400px]">
                              <input v-model.number="qzStore.selectedBarcodeMarginConfig.top" type="number" placeholder="Top"
                                class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedBarcodeMarginConfig.right" type="number"
                                placeholder="Right" class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedBarcodeMarginConfig.bottom" type="number"
                                placeholder="Bottom" class="border px-2 py-2 w-full rounded" />
                              <input v-model.number="qzStore.selectedBarcodeMarginConfig.left" type="number" placeholder="Left"
                                class="border px-2 py-2 w-full rounded" />
                            </div>
                          </div>

                          <!-- Scale -->
                          <div class="flex gap-2">
                            <label class="w-28 text-sm text-gray-600 text-left">Scale (%)</label>
                            <div class="flex-1 max-w-[400px]">
                              <input v-model.number="qzStore.selectedBarcodeScaleConfig" type="number" min="10" max="200"
                                class="border px-2 py-2 rounded w-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionPanel>
                </Accordion>
              </div>

              <div class="mt-4 flex justify-end">
                <!-- <button
                      @click="showPrinterConfig = false"
                      class="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition"
                    >
                      Close
                    </button> -->
              </div>
            </div>
          </div>
        </transition>

        <div class="space-y-4 mt-2">
          <div>
            <p class="text-sm text-gray-600">QZ Tray Setup:</p>
            <a href="/qz-certificate-import.html" target="_blank" rel="noopener"
              class="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
              <i class="pi pi-external-link"></i>
              How to turn on one-click fast printing.
            </a>
          </div>
          <div>
            <p class="text-sm text-gray-600">Branch Name:</p>
            <p class="font-medium">
              <template v-if="isLoadingBranchName">
                <i class="pi pi-spinner pi-spin mr-2"></i> Loading...
              </template>
              <template v-else>
                {{ branchName || 'Not available' }}
              </template>
            </p>
          </div>

          <div>
            <p class="text-sm text-gray-600">Download Status:</p>
            <p class="font-medium">
              {{ downloadedFilesSummary.isLoading ? 'Loading...' : 'Complete' }}
              ({{ downloadedFilesSummary.loadingPercentage }}%)
            </p>
          </div>

          <div>
            <p class="text-sm text-gray-600">Downloaded Files:</p>
            <div class="bg-gray-100 p-3 rounded max-h-40 overflow-y-auto">
              <template v-if="downloadedFileNames.length === 0">
                <p class="text-gray-500">No files loaded yet</p>
              </template>
              <template v-else>
                <ul class="space-y-1">
                  <li v-for="(fileName, index) in downloadedFileNames" :key="index" class="text-sm">
                    {{ fileName }}
                  </li>
                </ul>
              </template>
            </div>
          </div>

          <div>
            <p class="text-sm text-gray-600">Products Loaded:</p>
            <p class="font-medium">{{ downloadedFilesSummary.productFiles }} items</p>
          </div>

          <div>
            <p class="text-sm text-gray-600">Taxes Loaded:</p>
            <p class="font-medium">{{ downloadedFilesSummary.taxFiles }} items</p>
          </div>
        </div>

        <div class="mt-6 flex justify-between items-center">
          <button v-if="authStore.isManagerLoggedIn" @click="orderStore.clearCache" class="hover:text-purple-600">Clear
            Cache</button>
          <!-- <button 
                @click="showBranchInfo = false"
                class="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition"
              >
                Close
              </button> -->
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for the file list */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Custom checkbox styling to make it bigger */

.p-checkbox {
  border: 1px solid #ccc !important;
  border-radius: 0.375rem !important
}
.custom-checkbox :deep(.p-checkbox-box) {
  width: 2rem !important;
  height: 2rem !important;
  border-radius: 0.375rem !important;
}

.custom-checkbox :deep(.p-checkbox-icon) {
  font-size: 1.25rem !important;
}

.custom-checkbox :deep(.p-checkbox) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Connected */
.checkbox-connected :deep(.p-checkbox-box) {
  background-color: #a855f7 !important;
  border-color: #a855f7 !important;
}
.checkbox-connected :deep(.p-checkbox-icon) {
  color: #ffffff !important;
}

/* Disconnected */
.checkbox-disconnected :deep(.p-checkbox-box) {
  background-color: #ffffff !important;
  border-color: #9ca3af !important;
}
.checkbox-disconnected :deep(.p-checkbox-icon) {
  color: transparent !important;
}

/* PrimeVue Select theming for printers */
/* Neutral gray borders for PrimeVue Select (collapsed and expanded) */
:deep(.p-select) {
  border: 1px solid #d1d5db !important; /* gray-300 */
  border-radius: 0.5rem !important;
  padding: 0.5rem;
}
:deep(.p-select:not(.p-disabled):hover) {
  border-color: #9ca3af !important; /* gray-400 */
}
:deep(.p-select:focus-within),
:deep(.p-select.p-focus) {
  box-shadow: none !important;
  border-color: #9ca3af !important; /* gray-400 */
}
:deep(.p-select-panel) {
  border: 1px solid #d1d5db !important; /* gray-300 */
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-right-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

:deep(.p-accordionheader) {
  border: none !important;
  padding: 1rem;
  background-color: #f3f4f6 !important;
}

:deep(.p-accordionpanel) {
  border: none !important;
  margin-bottom: 1rem !important;
}

:deep(.p-accordioncontent-content) {
  border: none !important;
}

</style>